import '@data/Simulations/pipeline/blocks/ShgPotentialsBlock';
import type { ShgPotentialsBlockParameters, ShgPotentialsStrategy } from '@data/Simulations/pipeline/blocks/ShgPotentialsBlock';
import { GroupLabel } from '@gui/components/DesignSchemes/DesignSchemeView';
import { getSentence } from '@gui/constants/sentences';
import { SOLAR_MONTH_TO_FRAME } from '@gui/constants/SolarMonths';
import { ControllerManager } from '@gui/controller/ControllerManager';
import usePersistentStore, { useGlobalBucket } from '@gui/store/persistent/persistentStore';
import { Box, MenuItem, Select, Slider, Tooltip, Typography, useTheme } from '@mui/material';
import { CheckCircle, InfoCircle, XCircle } from '@untitledui-pro/icons/line';
import { SOLAR_HEAT_GAIN_STRATEGIES, type BlockParameters } from '@visualizer/types';
import React, { useEffect, useMemo } from 'react';
import { computeTotalEnergyFromSensors } from 'Utils/EnergyUtils';
import { formatNumberWithApostrophes } from 'Utils/MetricFormatting';
import { usePipelineBlockParams } from './usePipelineBlockParams';

type ShgPotentialsParametersProps = {
    blockId: string;
};

const USER_GUIDE_URL = 'https://urbanmetrix.com/guide/use-the-visualizer/filters#solar-heat-gains';
const FormatValue = (value: number): string => formatNumberWithApostrophes(value, { minimumFractionDigits: 0, maximumFractionDigits: 0 });

export const getShgPotentialsDefaultParams = (): Pick<
    ShgPotentialsBlockParameters,
    'strategy' | 'demand' | 'gValue' | 'tolerance' | 'maxIterations' | 'frame'
> => ({
    strategy: SOLAR_HEAT_GAIN_STRATEGIES.MATCH_HEATING_PEAKS,
    demand: 0,
    gValue: 60,
    tolerance: 1000,
    maxIterations: 20,
    frame: 0,
});

const getShgStrategyTooltip = (): React.ReactNode => {
    return (
        <Typography
            sx={{
                fontSize: '10.5px',
                lineHeight: 1.4,
                color: 'text.secondary',
            }}
        >
            {getSentence('Pipeline.Filters.ShgPotentials.MatchHeatingPeaks.Tooltip')}{' '}
            <a href={USER_GUIDE_URL} target="_blank" rel="noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>
                Learn more
            </a>
        </Typography>
    );
};

const renderShgStrategyMenuLabel = (label: string) => (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.35 }}>
        <Tooltip placement="top" title={getShgStrategyTooltip()}>
            <span style={{ display: 'inline-flex', alignItems: 'center', lineHeight: 0 }}>
                <InfoCircle className="icon-size-small" />
            </span>
        </Tooltip>
        <Typography sx={{ fontSize: '10px' }}>{label}</Typography>
    </Box>
);

const ShgPotentialsParameters: React.FC<ShgPotentialsParametersProps> = ({ blockId }) => {
    const theme = useTheme();
    const isLightTheme = theme.palette.mode === 'light';
    const matchedColor = isLightTheme ? '#1f7a1f' : '#00ff00';
    const unmatchedColor = isLightTheme ? '#b42318' : '#ff0000';

    // Controllers:
    const pipelineController = useMemo(() => ControllerManager.getInstance().getPipelineController(), []);

    // Zustand variables:
    const params = usePipelineBlockParams<ShgPotentialsBlockParameters>(blockId);
    const { solarHeatGains, buildingPatterns, energyDemand, demandPeaks } = useGlobalBucket((s) => s.solarEnergyResources);
    const isMatchingDemand = useGlobalBucket((s) => s.solarEnergyResources.solarMatching.isMatchingDemand);
    const hasPipelineExecuted = usePersistentStore((state) => state.getActiveLayerState().pipeline.executionCount);

    // Other variables:
    const strategyOptions: ShgPotentialsStrategy[] = [SOLAR_HEAT_GAIN_STRATEGIES.MATCH_HEATING_PEAKS];

    // Handlers:
    const updateParams = (partial: Partial<ShgPotentialsBlockParameters>) => {
        pipelineController.updateBlockParams(blockId, partial as BlockParameters);
    };

    // Effects:
    useEffect(() => {
        const defaults = getShgPotentialsDefaultParams();
        if (!params) {
            updateParams(defaults);
            return;
        }

        const missing: Partial<ShgPotentialsBlockParameters> = {};
        if (params.strategy == null) missing.strategy = defaults.strategy;
        if (params.demand == null) missing.demand = defaults.demand;
        if (params.gValue == null) missing.gValue = defaults.gValue;
        if (params.tolerance == null) missing.tolerance = defaults.tolerance;
        if (params.maxIterations == null) missing.maxIterations = defaults.maxIterations;
        if (params.frame == null) missing.frame = defaults.frame;

        if (Object.keys(missing).length > 0) {
            updateParams(missing);
        }
    }, [params]);

    const grossFloorSurface = Number(buildingPatterns.grossFloorSurface.value ?? 0);

    const monthlyHeatingRows = demandPeaks.heatingPeaks.type
        .map((type, i) => ({ type, occurrence: demandPeaks.heatingPeaks.occurrences[i], value: demandPeaks.heatingPeaks.values[i] }))
        .filter((row) => row.type === 'Monthly');
    const selectedMonthlyHeatingRow = monthlyHeatingRows.find((row) => row.occurrence === energyDemand.heatingPeakMonth);
    const fallbackMonthlyHeatingRow = monthlyHeatingRows[0];
    const peakRateFromDemandPeaks = Number(selectedMonthlyHeatingRow?.value ?? fallbackMonthlyHeatingRow?.value ?? 0);
    const energyDemandM2 =
        Number.isFinite(peakRateFromDemandPeaks) && peakRateFromDemandPeaks > 0
            ? peakRateFromDemandPeaks
            : Number(energyDemand.heatingPeakRate || 0);

    const demandToMatch = Math.max(0, grossFloorSurface) * Math.max(0, energyDemandM2);
    const frameToShow = SOLAR_MONTH_TO_FRAME[energyDemand.heatingPeakMonth as keyof typeof SOLAR_MONTH_TO_FRAME] ?? 0;

    // Update peak demand if needed:
    useEffect(() => {
        if (!params) return;
        const nextParams: Partial<ShgPotentialsBlockParameters> = {};
        if (params.demand !== demandToMatch) nextParams.demand = demandToMatch;
        if (params.frame !== frameToShow) nextParams.frame = frameToShow;
        if (Object.keys(nextParams).length > 0) updateParams(nextParams);
    }, [params?.demand, params?.frame, demandToMatch, frameToShow]);

    if (!params) return null;

    // Compute current transmitted energy when params changes:
    const gValue = params.gValue ?? solarHeatGains.gValue?.value ?? 60;
    const sensorsValue = useMemo(() => {
        const result = pipelineController.getLastResult();
        if (!result?.data) return undefined;
        return computeTotalEnergyFromSensors(result.data);
    }, [pipelineController, hasPipelineExecuted]);

    const totalTransmittedEnergy = sensorsValue !== undefined ? sensorsValue * (gValue / 100) : (solarHeatGains.transmittedEnergy ?? 0);
    const showMatchStatus = hasPipelineExecuted > 0 && !isMatchingDemand && demandToMatch > 0;
    const isDemandMatched = showMatchStatus && totalTransmittedEnergy >= demandToMatch;

    // UI:
    const tinyLabelSx = {
        fontSize: '10px',
        whiteSpace: 'nowrap',
        flexShrink: 0,
    };
    const rowLabelSx = {
        fontSize: '10px',
        flex: 1,
        minWidth: 0,
        whiteSpace: 'normal',
        overflowWrap: 'anywhere',
        lineHeight: 1.2,
    };
    const rightFieldSx = {
        ml: 'auto',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        width: '60%',
        minWidth: 96,
        maxWidth: '100%',
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ width: '100%' }}>
                <Typography sx={{ ...rowLabelSx, textAlign: 'center' }}>Strategy</Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.35 }}>
                    <Select
                        size="small"
                        value={(params.strategy ?? SOLAR_HEAT_GAIN_STRATEGIES.MATCH_HEATING_PEAKS) as ShgPotentialsStrategy}
                        renderValue={(selected) => String(selected)}
                        onChange={(e) => updateParams({ strategy: e.target.value as ShgPotentialsStrategy })}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                        MenuProps={{
                            PaperProps: {
                                onMouseDown: (e: React.MouseEvent) => e.stopPropagation(),
                                onClick: (e: React.MouseEvent) => e.stopPropagation(),
                            },
                            MenuListProps: {
                                onMouseDown: (e: React.MouseEvent) => e.stopPropagation(),
                                onClick: (e: React.MouseEvent) => e.stopPropagation(),
                            },
                        }}
                        sx={{
                            width: '65%',
                            minWidth: 160,
                            '& .MuiInputBase-root, &.MuiInputBase-root': {
                                height: 22,
                                fontSize: '10px',
                            },
                            '& .MuiSelect-select': {
                                py: 0.25,
                            },
                        }}
                    >
                        {strategyOptions.map((option) => (
                            <MenuItem key={option} value={option} sx={{ fontSize: '10px' }}>
                                {renderShgStrategyMenuLabel(String(option))}
                            </MenuItem>
                        ))}
                    </Select>
                </Box>
            </Box>

            <Box sx={{ width: '100%', mt: 0.75 }}>
                <Typography sx={{ ...rowLabelSx, textAlign: 'center' }}>Demand to be matched</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mt: 0.35 }}>
                    <Typography sx={{ ...tinyLabelSx, fontWeight: 600, fontSize: '11px' }}>{FormatValue(demandToMatch)}</Typography>
                    <Typography sx={tinyLabelSx}>[kWh/y]</Typography>
                </Box>
            </Box>

            <GroupLabel label="Inputs" mt={0.75} />

            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, width: '100%', mt: 0.75 }}>
                <Typography sx={rowLabelSx}>G-Value</Typography>
                <Box sx={{ ...rightFieldSx, mt: 0.1 }}>
                    <Slider
                        className="panel-slider"
                        size="small"
                        value={gValue}
                        min={solarHeatGains.gValue?.minValue ?? 0}
                        max={solarHeatGains.gValue?.maxValue ?? 100}
                        step={1}
                        onChange={(_, val) => updateParams({ gValue: Array.isArray(val) ? val[0] : val })}
                        valueLabelDisplay="on"
                        sx={{ flex: 1, minWidth: 0 }}
                    />
                    <Typography sx={tinyLabelSx}>[%]</Typography>
                </Box>
            </Box>

            <GroupLabel label="Results" mt={0.75} />

            <Box sx={{ width: '100%', mt: 0.75 }}>
                <Typography sx={{ ...rowLabelSx, textAlign: 'center' }}>Total Transmitted Energy</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5, mt: 0.35 }}>
                    <Typography sx={{ ...tinyLabelSx, fontWeight: 600, fontSize: '11px' }}>
                        {FormatValue(totalTransmittedEnergy)}
                    </Typography>
                    <Typography sx={tinyLabelSx}>[kWh/y]</Typography>
                </Box>
            </Box>

            {showMatchStatus && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, mt: 0.75, width: '100%' }}>
                    {isDemandMatched ? (
                        <>
                            <CheckCircle color={matchedColor} style={{ width: 14, height: 14 }} />
                            <Typography sx={{ ...tinyLabelSx, color: matchedColor }}>Demand was matched</Typography>
                        </>
                    ) : (
                        <>
                            <XCircle color={unmatchedColor} style={{ width: 14, height: 14 }} />
                            <Typography sx={{ ...tinyLabelSx, color: unmatchedColor }}>Demand wasn't matched</Typography>
                        </>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default ShgPotentialsParameters;
